import React, { useState, useRef } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Box,
  Toolbar,
  Tooltip,
  Collapse,
  ListItemIcon,
  Popper,
  Paper,
  Grow,
} from "@mui/material";
import {
  Logout,
  Brightness4,
  Brightness7,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";

import { useNavigate, useLocation } from "react-router-dom";
import { useThemeMode } from "../theme/ThemeContextProvider";
import { useSessionAuth } from "../auth/SessionAuthContext";
import navConfig from "../routes/nav.config.json";
import { iconMap } from "../routes/componentIconMap";

const Sidebar = ({
  open,
  variant = "persistent",
  onClose,
  drawerWidth,
  miniVariant,
  toggleMini,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleTheme, mode } = useThemeMode();
  const { logout, roles } = useSessionAuth();

  const miniWidth = 75;
  const [openSubmenu, setOpenSubmenu] = useState({});
  const [hoverAnchor, setHoverAnchor] = useState(null);
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const hoverTimeoutRef = useRef(null);

  const toggleSubmenu = (label) =>
    setOpenSubmenu((prev) => ({ ...prev, [label]: !prev[label] }));

  const isAllowed = (allowedRoles = []) =>
    allowedRoles.length === 0 || allowedRoles.some((role) => roles?.[role]);

  const parentItems = navConfig
    .filter((item) => item.parent === 0 && isAllowed(item.allowedRoles) && item.ShowInMenu !== false)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  const getChildren = (parentId) =>
    navConfig
      .filter((item) => item.parent === parentId && isAllowed(item.allowedRoles) && item.ShowInMenu !== false)
      .sort((a, b) => a.displayOrder - b.displayOrder);

  const dynamicMenuItems = parentItems.map((parent) => {
    const children = getChildren(parent.id);
    return {
      label: parent.label,
      icon: iconMap[parent.icon] || null,
      path: parent.url,
      children: children.length
        ? children.map((child) => ({
            label: child.label,
            path: child.url,
            icon: iconMap[child.icon] || null,
          }))
        : undefined,
    };
  });

  const menuItems = [
    ...dynamicMenuItems,
    {
      label: `${mode === "light" ? "Dark" : "Light"} Mode`,
      icon: mode === "light" ? <Brightness4 /> : <Brightness7 />,
      action: toggleTheme,
    },
    {
      label: "Logout",
      icon: <Logout />,
      action: logout,
    },
  ];

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: miniVariant ? miniWidth : drawerWidth,
        flexShrink: 0,
        whiteSpace: "nowrap",
        "& .MuiDrawer-paper": {
          width: miniVariant ? miniWidth : drawerWidth,
          boxSizing: "border-box",
          transition: "width 0.3s",
          overflow: "visible",
        },
      }}
    >
      <Toolbar />
      {/* <Box display="flex" justifyContent="center" mt={0.2}>
        <IconButton onClick={toggleMini}>
          {miniVariant ? "»" : "«"}
        </IconButton>
      </Box> */}

      <List>
        {menuItems.map(({ label, icon, path, action, children }) => {
          const isSelected = location.pathname === path;
          const isExpanded = openSubmenu[label];

          const handleClick = () => {
            if (children) {
              toggleSubmenu(label);
            } else {
              if (path) navigate(path);
              if (action) action();
              if (onClose && variant === "temporary") onClose();
            }
          };

          return (
            <Box key={label} sx={{ position: "relative" }}>
              {/* <Tooltip title={miniVariant ? label : ""} placement="right"> */}
                <ListItemButton
                  selected={isSelected}
                  onClick={handleClick}
                  onMouseEnter={(e) => {
                    if (children && miniVariant) {
                      clearTimeout(hoverTimeoutRef.current);
                      setHoverAnchor(e.currentTarget);
                      setHoveredMenu(label);
                    }
                  }}
                  onMouseLeave={() => {
                    if (miniVariant) {
                      hoverTimeoutRef.current = setTimeout(() => {
                        setHoveredMenu(null);
                        setHoverAnchor(null);
                      }, 300);
                    }
                  }}
                  sx={{
                    justifyContent: miniVariant ? "center" : "flex-start",
                    px: 2,
                    py: 0.9,
                    borderRadius: 0,
                    "&.Mui-selected": {
                      backgroundColor: (theme) => theme.palette.action.selected,
                    },
                    "&:hover": {
                      backgroundColor: (theme) => theme.palette.action.hover,
                    },
                  }}
                >
                  {miniVariant ? (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      width="100%"
                      sx={{ lineHeight: 1, textAlign: "center" }}
                    >
                      <Box>{icon}</Box>
                      <Box
                        component="span"
                        sx={{
                          fontSize: "0.70rem",
                          mt: 0.5,
                          px: 0.5,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: "inherit",
                        }}
                      >
                        {label}
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>{icon}</ListItemIcon>
                      <ListItemText primary={label} />
                      {children && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
                    </>
                  )} 
                </ListItemButton>
              {/* </Tooltip> */}

              {!miniVariant && children && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  
                  <List component="div" disablePadding>
                    {children.map((child) => (
                      <ListItemButton
                        key={child.label}
                        selected={location.pathname === child.path}
                        onClick={() => {
                          navigate(child.path);
                          if (onClose && variant === "temporary") onClose();
                        }}
                        sx={{
                          pl: 6,
                          py: 1,
                          borderRadius: 0,
                          "&.Mui-selected": {
                            backgroundColor: (theme) =>
                              theme.palette.action.selected,
                          },
                        }}
                      >
                        <ListItemText primary={child.label} />
                      </ListItemButton>
                    ))}


                  </List>
                </Collapse>
              )}

              {miniVariant && children && hoveredMenu === label && (
                <Popper
                  open
                  anchorEl={hoverAnchor}
                  placement="right-start"
                  disablePortal
                  onMouseEnter={() => {
                    clearTimeout(hoverTimeoutRef.current);
                  }}
                  onMouseLeave={() => {
                    hoverTimeoutRef.current = setTimeout(() => {
                      setHoveredMenu(null);
                      setHoverAnchor(null);
                    }, 300);
                  }}
                  modifiers={[
                    {
                      name: "offset",
                      options: {
                        offset: [0, 4],
                      },
                    },
                  ]}
                  style={{ zIndex: 1300 }}
                >
                  <Grow in>
                    <Paper
                      elevation={4}
                      sx={{
                        minWidth: 180,
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[900]
                            : theme.palette.grey[100],
                      }}
                    >
                      <List dense>
                        {children.map((child) => (
                          <ListItemButton
                            key={child.label}
                            onClick={() => {
                              navigate(child.path);
                              if (onClose && variant === "temporary") onClose();
                            }}
                            selected={location.pathname === child.path}
                            sx={{
                              "&.Mui-selected": {
                                backgroundColor: (theme) =>
                                  theme.palette.action.selected,
                              },
                            }}
                          >
                             {child.icon && (
                                <ListItemIcon sx={{ minWidth: 32 }}>{child.icon}</ListItemIcon>
                              )}
                            <ListItemText primary={child.label} />
                          </ListItemButton>
                        ))}
                      </List>
                    </Paper>
                  </Grow>
                </Popper>
              )}
            </Box>
          );
        })}
      </List>
    </Drawer>
  );
};

export default Sidebar;
